import { canvasCenterElement, convertObjectFromOldPositionToNewPosition } from '@/commons/fabricjs/FabricPositionHelper';
import { applyTextDefaultConfig } from '@/commons/fabricjs/FabricTextHelper';
import { FINAL_IMAGE_HEIGHT, FINAL_IMAGE_WIDTH } from '@/guidetti/projectConstantes';
import { Canvas, Group, Line, FabricText, Point, type TPointerEventInfo } from 'fabric';
import type { IFabricWorldInitParams } from '@/commons/fabricjs/IFabricWorldInitParams';

class FabricWorld {
    fabricCanvas: Canvas | null;
    text: FabricText | null;
    canvasWidth: number;
    canvasHeight: number;

    zoomCallback: (opt: TPointerEventInfo) => void;

    imageMargin: { top?: number; right?: number; bottom?: number; left?: number } = {};
    _clickRefPoint: Point | null = null;
    panCallback: (opt: TPointerEventInfo) => void;

    clickCallback: (opt: TPointerEventInfo) => void;

    constructor() {
        this.fabricCanvas = null;
    }

    init(params: IFabricWorldInitParams) {
        const { canvas, ...fabricOptions } = params;
        if (!canvas) {
            throw new Error('Canvas element is null');
        }
        // We default size to parent rendering size
        // sonarlint is wrong because we want 0 to go to default value
        this.canvasWidth = canvas.parentElement?.getBoundingClientRect().width || 300;
        this.canvasHeight = canvas.parentElement?.getBoundingClientRect().height || 150;

        canvas.width = this.canvasWidth;
        canvas.height = this.canvasHeight;
        this.fabricCanvas = new Canvas(canvas, fabricOptions);
    }

    /**
     * Usefull to set a reference point when user click on canvas outside FabricWorld instance
     * used by panCallback and clickCallback
     * @param point Point object or null
     */
    setClickedRefPoint = (point: Point | null) => {
        this._clickRefPoint = point;
    };

    /**
     * Usefull to get a reference point when user click on canvas outside FabricWorld instance
     * used by panCallback and clickCallback
     * @returns Point object or null
     */
    getClickedRefPoint = () => {
        return this._clickRefPoint;
    };

    /**
     * usefull to get delta between real final image
     */
    get scaleFactor() {
        return this.canvasWidth / FINAL_IMAGE_WIDTH;
    }

    /**
     * Return a canvas element that you may use as a texture in a 3D object
     */
    getTexture(): HTMLCanvasElement | undefined {
        return this.fabricCanvas?.toCanvasElement();
    }

    addText(text = 'default text') {
        this.text = new FabricText(text, {
            // if you want to change 'name' attributes here you might also wants to change it in
            // toDatalessJSON and to unserializing function
            name: 'textName',
        });
        applyTextDefaultConfig(this.text);
        this.fabricCanvas?.add(this.text);
        // this.fabricCanvas?.setActiveObject(this.text);
        canvasCenterElement(this, this.text);
    }

    /**
     * Create a group of guides (center, etc), add them to
     * the canvas and automaticaly remove from final
     * render when creating a texture.
     * Use super.addGuides() inside an herited class to get existantes guides and add some more.
     * @returns Group object containing the guides already loaded inside Fcanvas
     */
    addGuides(horizontal = true, vertical = true) {
        // consider also adding overlay image fabricCanvas.setOverlayImage
        const stroke = '#bb00cc';
        const lines = new Group([], {
            selectable: false,
            evented: false,
        });
        // Center
        const halfWidth = Math.round(FINAL_IMAGE_WIDTH / 2);
        const halfHeight = Math.round(FINAL_IMAGE_HEIGHT / 2);
        if (horizontal) {
            lines.add(
                new Line([0, halfHeight, FINAL_IMAGE_WIDTH, halfHeight], {
                    stroke,
                    selectable: false,
                    evented: false,
                }),
            );
        }
        if (vertical) {
            lines.add(
                // vertical line
                new Line([halfWidth, 0, halfWidth, FINAL_IMAGE_HEIGHT], {
                    stroke,
                    selectable: false,
                    evented: false,
                }),
            );
        }

        this.fabricCanvas?.add(lines);
        return lines;
    }

    serialize() {
        const jsonProject = this.fabricCanvas?.toDatalessJSON(['name']);
        jsonProject.percentUtils = {
            canvasW: this.canvasWidth,
            canvasH: this.canvasHeight,
        };
        return jsonProject;
    }

    unserialize(jsonProject: { percentUtils?: { canvasW: number; canvasH: number } }) {
        // this.fabricCanvas?.clear();
        this.fabricCanvas?.renderAll();
        this.fabricCanvas
            ?.loadFromJSON(jsonProject, (jsonObject, fObject) => {
                // @ts-ignore in the future you might want to create a class that extend fabric text which include all you need
                fObject.name = jsonObject.name;
            })
            .then(() => {
                this.fabricCanvas?.getObjects().forEach((fObject) => {
                    // Some config are not saved, so we reapply them by type
                    if (fObject.type === 'group') {
                        const group = fObject as Group;
                        group.selectable = false;
                        group.evented = false;
                    } else if (fObject.type === 'text') {
                        applyTextDefaultConfig(fObject as FabricText);
                        // this.fabricCanvas?.setActiveObject(fObject);
                    }

                    // convert position to new canvas size (fix retina or devicePixelRatio issues)
                    convertObjectFromOldPositionToNewPosition(
                        fObject,
                        jsonProject.percentUtils?.canvasW,
                        jsonProject.percentUtils?.canvasH,
                        this.canvasWidth,
                        this.canvasHeight,
                    );

                    // force update of object
                    fObject.setCoords(); // update handle positions
                    fObject.set('dirty', true);
                });
                this.fabricCanvas?.renderAll();
            });
    }

    dispose(): void {
        if (!this.fabricCanvas) {
            return;
        }
        // destroy event listeners here
        this.fabricCanvas.off('mouse:wheel', this.zoomCallback);
        this.fabricCanvas.off('mouse:down', this.clickCallback);
        this.fabricCanvas.off('mouse:move', this.panCallback);
        this.fabricCanvas.off('mouse:up', this.clickCallback);
        this.fabricCanvas.dispose();
        this.fabricCanvas = null;
    }
}

export { FabricWorld };
